import { useMemo, useState } from 'react';

import { studentNavGroups } from './studentNav';
import { StudentTopBar } from './StudentTopBar';
import { MobileNavDrawer } from './MobileNavDrawer';

import { AppSidebar } from '@/components/AppSidebar';
import { PreviewBanner } from '@/components/PreviewBanner';
import { useAuthStore } from '@/stores/authStore';
import { useCurrentBatch } from '@/lib/hooks/useCurrentBatch';
import { useStreak } from '@/lib/hooks/useHome';
import { useUnreadCount } from '@/lib/hooks/useNotifications';
import { useStudentThread } from '@/lib/hooks/useMessages';

export function StudentLayout({ children }: { children: React.ReactNode }) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { batchId } = useCurrentBatch();
  const streakDays = useStreak(batchId ?? undefined);
  const unreadCount = useUnreadCount();
  const { user } = useAuthStore();
  const { data: thread } = useStudentThread();
  const lastMessage = thread?.messages?.[thread.messages.length - 1];
  const msgCount = lastMessage && lastMessage.sender_id !== user?.id ? 1 : 0;

  const navGroups = useMemo(
    () => studentNavGroups.map((group) => ({
      ...group,
      items: group.items.map((item) =>
        item.to === '/messages' ? { ...item, badge: msgCount } : item,
      ),
    })),
    [msgCount],
  );

  return (
    <div className="flex h-screen overflow-hidden">
      <div className="hidden lg:flex">
        <AppSidebar
          navGroups={navGroups}
          storageKey="student-sidebar-collapsed"
          settingsPath="/settings"
        />
      </div>
      <MobileNavDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />
      <div className="flex min-w-0 flex-1 flex-col">
        <StudentTopBar streakDays={streakDays} unreadCount={unreadCount} onMenu={() => setDrawerOpen(true)} />
        <PreviewBanner />
        <main
          className="flex-1 overflow-y-auto"
          style={{
            backgroundImage: [
              'radial-gradient(circle, rgba(26,23,20,0.12) 1px, transparent 1px)',
              'radial-gradient(ellipse 900px 700px at top right, rgba(232, 102, 13, 0.18) 0%, transparent 65%)',
              'radial-gradient(ellipse 500px 400px at 5% 95%, rgba(124, 58, 237, 0.10) 0%, transparent 55%)',
            ].join(', '),
            backgroundSize: '22px 22px, auto, auto',
            backgroundColor: '#faf8f6',
          }}
        >
          {children}
        </main>
      </div>
    </div>
  );
}
